import React from 'react';

import Select from '@material-ui/core/Select';
import SvgIcon from '@material-ui/core/SvgIcon';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';

import s from './SimpleSelect.module.scss';

type SimpleSelectProps = {
  disabled: boolean,
  label: string,
  value: any,
  onChange: () => void,
  options: any,
};

/**
 * Since the icon is custom, we modify the select's icon
 * class to center it properly. There is also a small bug
 * when the input is in `focus` where the background becomes
 * white but it goes in front of the label and covers it.
 * The select's `focus` property is changed here too.
 */

const VirtuaStyleSelect = withStyles({
  icon: {
    marginRight: '6px',
    marginTop: '3px',
  },
  select: {
    '&:focus': {
      backgroundColor: 'transparent',
    },
  },
})(Select);

/**
 * This menu item is styled like the Virtualized Select being
 * used in the Electro components used in forms.
 */

const VirtuaStyleMenuItem = withStyles({
  root: {
    '&:hover': {
      color: '#3984b6',
      backgroundColor: '#f4f4f3',
    },
    '&$selected': {
      backgroundColor: 'white',
    },
    '&$selected:hover': {
      backgroundColor: '#f4f4f3',
    },
  },
  selected: {
    color: 'black',
    fontWeight: '700',
    '&:hover': {
      color: '#3984b6',
      backgroundColor: '#f4f4f3',
    },
  },
})(MenuItem);

/**
 * These menu props will make the dropdown pop up look
 * like the Virtualized Select's UI.
 */

const menuProps = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
  PaperProps: {
    elevation: 0,
    style: {
      maxHeight: 200,
      overflowY: 'auto',
      borderColor: '#eaebec',
      borderWidth: '1px',
      borderStyle: 'solid',
    },
  },
  MenuListProps: {
    disablePadding: true,
  },
};

/** Since the Material UI select passes props for classnames
 * to any custom icon and it also works properly only with an
 * SvgIcon component, we create this function.
 *
 * For the `disabled` prop, unfortunately, the select doesn't pass
 * the `SvgIcon` a `color` prop or another `disabled` `classname`, so it
 * doesn't know when it has this state. To circumvent this, the main
 * component's `disabled` prop is passed here and the `fill` property of
 * the path is directly modified since even the `color` prop is overriden
 * by the `select-icon` class default colors.
 */

function ArrowIcon(props) {
  const { disabled } = props;
  const arrowColor = disabled ? '#a3adb1' : '#f26045';
  return (
    <SvgIcon {...props}>
      <path
        d="M17.8379 9.15245C17.7179 9.06534 17.5738 9 17.4057 9H6.6003C6.26413 9 6 9.23956 6 9.54446C6
        9.69691 6.07204 9.82759 6.16808 9.93648L11.5708 14.8367C11.8109 15.0544 12.1951 15.0544 12.4112
        14.8367L17.8379 9.93648C18.054 9.71869 18.054 9.37024 17.8379 9.15245ZM12.003 13.6824L8.04102
        10.0889H15.965L12.003 13.6824Z"
        fill={arrowColor}
      />
    </SvgIcon>
  );
}

/**
 * A basic Select component with label, disabled state and handling of changes, it's own value and options.
 * It's styled to match the Virtualized Select component UI.
 */

function SimpleSelect(props: SimpleSelectProps) {
  const { disabled, label, value, onChange, options } = props;

  return (
    <div className={s['select-container']}>
      <FormControl>
        <InputLabel disabled={disabled} shrink={Boolean(value)}>
          {label}
        </InputLabel>
        <VirtuaStyleSelect
          disabled={disabled}
          IconComponent={selectIconProps => ArrowIcon({ disabled, ...selectIconProps })}
          MenuProps={menuProps}
          value={value}
          onChange={onChange}
        >
          {options.map(option => (
            <VirtuaStyleMenuItem key={option.name} value={option.name}>
              {option.name}
            </VirtuaStyleMenuItem>
          ))}
        </VirtuaStyleSelect>
      </FormControl>
    </div>
  );
}

export default SimpleSelect;
